import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Send, Terminal, Server, DollarSign, 
  CreditCard, AlertCircle, CheckCircle, Loader, Lock,
  MapPin, Hash, Key, Building, Globe, Shield, Database,
  Radio, Wifi, HardDrive, User, Phone, FileText, Clock
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { accountAPI, transferAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import S2SConnectionAnimation from '../components/S2SConnectionAnimation';
import TransferProcessingAnimation from '../components/TransferProcessingAnimation';

const TRANSFER_TYPES = [
  { id: 'Terminal', label: 'TERMINAL', icon: Terminal, color: 'text-cyan-400' },
  { id: 'SWIFT', label: 'SWIFT', icon: Globe, color: 'text-blue-400' },
  { id: 'SEPA', label: 'SEPA', icon: Building, color: 'text-purple-400' },
  { id: 'MoneyGram', label: 'MONEYGRAM', icon: Send, color: 'text-orange-400' },
  { id: 'Western Union', label: 'WESTERN UNION', icon: DollarSign, color: 'text-yellow-400' },
  { id: 'ACH', label: 'ACH', icon: Database, color: 'text-green-400' },
  { id: 'S2S', label: 'SERVER TO SERVER', icon: Server, color: 'text-red-400' },
];

const AdvancedTransferPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [transferType, setTransferType] = useState(searchParams.get('type') || 'Terminal');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [recipientInfo, setRecipientInfo] = useState('');
  
  // Terminal Transfer fields
  const [terminalId, setTerminalId] = useState('');
  const [terminalLocation, setTerminalLocation] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [pinCode, setPinCode] = useState('');
  
  // SWIFT fields
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryBank, setBeneficiaryBank] = useState('');
  const [beneficiarySwift, setBeneficiarySwift] = useState('');
  const [beneficiaryIban, setBeneficiaryIban] = useState('');
  const [beneficiaryAddress, setBeneficiaryAddress] = useState('');
  const [swiftReference, setSwiftReference] = useState('');
  
  // SEPA fields
  const [sepaIban, setSepaIban] = useState('');
  const [sepaBic, setSepaBic] = useState('');
  const [sepaReference, setSepaReference] = useState('');
  
  // MoneyGram fields
  const [mgReceiverName, setMgReceiverName] = useState('');
  const [mgReceiverPhone, setMgReceiverPhone] = useState('');
  const [mgReceiverCountry, setMgReceiverCountry] = useState('');
  const [mgReceiverCity, setMgReceiverCity] = useState('');
  const [mgReferenceNumber, setMgReferenceNumber] = useState('');
  
  // Western Union fields
  const [wuReceiverName, setWuReceiverName] = useState('');
  const [wuReceiverPhone, setWuReceiverPhone] = useState('');
  const [wuReceiverCountry, setWuReceiverCountry] = useState('');
  const [wuReceiverAddress, setWuReceiverAddress] = useState('');
  const [wuMtcn, setWuMtcn] = useState('');
  const [wuSecurityQuestion, setWuSecurityQuestion] = useState('');
  const [wuSecurityAnswer, setWuSecurityAnswer] = useState('');
  
  // ACH fields
  const [achRoutingNumber, setAchRoutingNumber] = useState('');
  const [achAccountNumber, setAchAccountNumber] = useState('');
  const [achAccountType, setAchAccountType] = useState('checking');
  const [achBankName, setAchBankName] = useState('');
  const [achAccountHolderName, setAchAccountHolderName] = useState('');
  
  // S2S fields
  const [s2sType, setS2sType] = useState('internal');
  const [recipientIp, setRecipientIp] = useState('');
  const [recipientPort, setRecipientPort] = useState('');
  const [internalAccountId, setInternalAccountId] = useState('');
  const [departmentCode, setDepartmentCode] = useState('');
  const [serverCertificate, setServerCertificate] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [recipientUsername, setRecipientUsername] = useState('');
  const [recipientPassword, setRecipientPassword] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showProcessing, setShowProcessing] = useState(false);
  const [showS2SConnection, setShowS2SConnection] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await accountAPI.getAccounts();
      setAccounts(response.data);
      if (response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
        setCurrency(response.data[0].currency);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId);
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      setCurrency(account.currency);
    }
  };

  const getActualTransferType = () => {
    if (transferType === 'S2S') {
      return s2sType === 'internal' ? 'S2S/Internal' : 'S2S/External';
    }
    return transferType;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    const account = accounts.find(acc => acc.id === selectedAccount);
    if (!account) {
      setError('Please select an account');
      setLoading(false);
      return;
    }

    if (parseFloat(amount) > account.balance) {
      setError(`Insufficient balance. Available: $${account.balance.toFixed(2)}`);
      setLoading(false);
      return;
    }

    // Show appropriate animation
    if (transferType === 'S2S' && s2sType === 'external') {
      setShowS2SConnection(true);
      setLoading(false);
      return;
    } else {
      setShowProcessing(true);
      setLoading(false);
      return;
    }
  };

  const handleProcessingComplete = async (success) => {
    setShowProcessing(false);
    
    if (!success) {
      setError('Transfer processing failed');
      return;
    }

    await submitTransfer();
  };

  const handleS2SComplete = async (success, message) => {
    setShowS2SConnection(false);
    
    if (!success) {
      setError(message || 'S2S connection failed');
      return;
    }

    await submitTransfer();
  };

  const submitTransfer = async () => {
    setLoading(true);

    const account = accounts.find(acc => acc.id === selectedAccount);

    const transferData = {
      from_account_id: selectedAccount,
      transfer_type: getActualTransferType(),
      amount: parseFloat(amount),
      currency: currency,
      recipient_info: recipientInfo,
    };

    // Add type-specific fields
    if (transferType === 'Terminal') {
      transferData.terminal_id = terminalId;
      transferData.terminal_location = terminalLocation;
      transferData.authorization_code = authCode;
      transferData.pin_code = pinCode;
    } else if (transferType === 'SWIFT') {
      transferData.beneficiary_name = beneficiaryName;
      transferData.beneficiary_bank = beneficiaryBank;
      transferData.beneficiary_swift = beneficiarySwift;
      transferData.beneficiary_iban = beneficiaryIban;
      transferData.beneficiary_address = beneficiaryAddress;
      transferData.swift_reference = swiftReference;
    } else if (transferType === 'SEPA') {
      transferData.sepa_iban = sepaIban;
      transferData.sepa_bic = sepaBic;
      transferData.sepa_reference = sepaReference;
      transferData.beneficiary_name = beneficiaryName;
    } else if (transferType === 'MoneyGram') {
      transferData.receiver_name = mgReceiverName;
      transferData.receiver_phone = mgReceiverPhone;
      transferData.receiver_country = mgReceiverCountry;
      transferData.receiver_city = mgReceiverCity;
      transferData.reference_number = mgReferenceNumber;
    } else if (transferType === 'Western Union') {
      transferData.receiver_name = wuReceiverName;
      transferData.receiver_phone = wuReceiverPhone;
      transferData.receiver_country = wuReceiverCountry;
      transferData.receiver_address = wuReceiverAddress;
      transferData.mtcn = wuMtcn;
      transferData.security_question = wuSecurityQuestion;
      transferData.security_answer = wuSecurityAnswer;
    } else if (transferType === 'ACH') {
      transferData.routing_number = achRoutingNumber;
      transferData.account_number = achAccountNumber;
      transferData.account_type = achAccountType;
      transferData.bank_name = achBankName;
      transferData.account_holder_name = achAccountHolderName;
    } else if (transferType === 'S2S') {
      transferData.s2s_type = s2sType;
      if (s2sType === 'internal') {
        transferData.internal_account_id = internalAccountId;
        transferData.department_code = departmentCode;
      } else {
        transferData.recipient_ip = recipientIp;
        transferData.recipient_port = parseInt(recipientPort);
        transferData.server_certificate = serverCertificate;
        transferData.auth_token = authToken;
        transferData.recipient_username = recipientUsername;
        transferData.recipient_password = recipientPassword;
      }
    }

    try {
      const response = await transferAPI.createTransfer(transferData);
      setResult(response.data);
      setSuccess(true);
      
      setTimeout(() => {
        resetForm();
        loadAccounts();
      }, 1000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setRecipientInfo('');
    setTerminalId('');
    setTerminalLocation('');
    setAuthCode('');
    setPinCode('');
    setBeneficiaryName('');
    setBeneficiaryBank('');
    setBeneficiarySwift('');
    setBeneficiaryIban('');
    setBeneficiaryAddress('');
    setSwiftReference('');
    setSepaIban('');
    setSepaBic('');
    setSepaReference('');
    setMgReceiverName('');
    setMgReceiverPhone('');
    setMgReceiverCountry('');
    setMgReceiverCity('');
    setMgReferenceNumber('');
    setWuReceiverName('');
    setWuReceiverPhone('');
    setWuReceiverCountry('');
    setWuReceiverAddress('');
    setWuMtcn('');
    setWuSecurityQuestion('');
    setWuSecurityAnswer('');
    setAchRoutingNumber('');
    setAchAccountNumber('');
    setAchAccountType('checking');
    setAchBankName('');
    setAchAccountHolderName('');
    setRecipientIp('');
    setRecipientPort('');
    setInternalAccountId('');
    setDepartmentCode('');
    setServerCertificate('');
    setAuthToken('');
    setRecipientUsername('');
    setRecipientPassword('');
  };

  const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);

  const renderTerminalFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-cyan-400 mb-2">
        <Terminal className="w-4 h-4" />
        TERMINAL TRANSFER DETAILS
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            TERMINAL ID *
          </label>
          <input
            type="text"
            value={terminalId}
            onChange={(e) => setTerminalId(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="TRM-XXXX-XXXX"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            TERMINAL LOCATION *
          </label>
          <input
            type="text"
            value={terminalLocation}
            onChange={(e) => setTerminalLocation(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="New York, USA"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <Key className="w-3 h-3" />
            AUTHORIZATION CODE *
          </label>
          <input
            type="text"
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm font-mono"
            placeholder="AUTH-XXXXXXXXXX"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <Lock className="w-3 h-3" />
            PIN CODE *
          </label>
          <input
            type="password"
            value={pinCode}
            onChange={(e) => setPinCode(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="****"
            maxLength="6"
            required
          />
        </div>
      </div>
    </motion.div>
  );

  const renderSwiftFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-blue-400 mb-2">
        <Globe className="w-4 h-4" />
        SWIFT INTERNATIONAL TRANSFER
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs mb-2">BENEFICIARY NAME *</label>
          <input
            type="text"
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="John Doe"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2">BENEFICIARY BANK *</label>
            <input
              type="text"
              value={beneficiaryBank}
              onChange={(e) => setBeneficiaryBank(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="Deutsche Bank AG"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2">SWIFT/BIC CODE *</label>
            <input
              type="text"
              value={beneficiarySwift}
              onChange={(e) => setBeneficiarySwift(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm uppercase font-mono"
              placeholder="DEUTDEFFXXX"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs mb-2">BENEFICIARY IBAN *</label>
          <input
            type="text"
            value={beneficiaryIban}
            onChange={(e) => setBeneficiaryIban(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm uppercase font-mono"
            placeholder="DE89370400440532013000"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2">BENEFICIARY BANK ADDRESS *</label>
          <textarea
            value={beneficiaryAddress}
            onChange={(e) => setBeneficiaryAddress(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            rows="2"
            placeholder="Taunusanlage 12, 60325 Frankfurt, Germany"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2">PAYMENT REFERENCE</label>
          <input
            type="text"
            value={swiftReference}
            onChange={(e) => setSwiftReference(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="Invoice #12345"
          />
        </div>
      </div>
    </motion.div>
  );

  const renderSepaFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-purple-400 mb-2">
        <Building className="w-4 h-4" />
        SEPA EUROPEAN TRANSFER
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs mb-2">BENEFICIARY NAME *</label>
          <input
            type="text"
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="Marie Dupont"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2">IBAN *</label>
            <input
              type="text"
              value={sepaIban}
              onChange={(e) => setSepaIban(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm uppercase font-mono"
              placeholder="FR1420041010050500013M02606"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2">BIC/SWIFT CODE *</label>
            <input
              type="text"
              value={sepaBic}
              onChange={(e) => setSepaBic(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm uppercase font-mono"
              placeholder="BNPAFRPPXXX"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs mb-2">PAYMENT REFERENCE</label>
          <input
            type="text"
            value={sepaReference}
            onChange={(e) => setSepaReference(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="Reference information"
          />
        </div>
      </div>
    </motion.div>
  );

  const renderMoneyGramFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-orange-400 mb-2">
        <Send className="w-4 h-4" />
        MONEYGRAM TRANSFER
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <User className="w-3 h-3" />
            RECEIVER FULL NAME *
          </label>
          <input
            type="text"
            value={mgReceiverName}
            onChange={(e) => setMgReceiverName(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="John Smith"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Phone className="w-3 h-3" />
              RECEIVER PHONE *
            </label>
            <input
              type="text"
              value={mgReceiverPhone}
              onChange={(e) => setMgReceiverPhone(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="+1234567890"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Globe className="w-3 h-3" />
              RECEIVER COUNTRY *
            </label>
            <input
              type="text"
              value={mgReceiverCountry}
              onChange={(e) => setMgReceiverCountry(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="United States"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <MapPin className="w-3 h-3" />
              RECEIVER CITY *
            </label>
            <input
              type="text"
              value={mgReceiverCity}
              onChange={(e) => setMgReceiverCity(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="New York"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Hash className="w-3 h-3" />
              REFERENCE NUMBER
            </label>
            <input
              type="text"
              value={mgReferenceNumber}
              onChange={(e) => setMgReferenceNumber(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              placeholder="Auto-generated"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderWesternUnionFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-yellow-400 mb-2">
        <DollarSign className="w-4 h-4" />
        WESTERN UNION TRANSFER
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <User className="w-3 h-3" />
            RECEIVER FULL NAME *
          </label>
          <input
            type="text"
            value={wuReceiverName}
            onChange={(e) => setWuReceiverName(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="John Smith"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Phone className="w-3 h-3" />
              RECEIVER PHONE *
            </label>
            <input
              type="text"
              value={wuReceiverPhone}
              onChange={(e) => setWuReceiverPhone(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="+1234567890"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Globe className="w-3 h-3" />
              RECEIVER COUNTRY *
            </label>
            <input
              type="text"
              value={wuReceiverCountry}
              onChange={(e) => setWuReceiverCountry(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="United States"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            RECEIVER ADDRESS *
          </label>
          <textarea
            value={wuReceiverAddress}
            onChange={(e) => setWuReceiverAddress(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            rows="2"
            placeholder="123 Main Street, New York, NY 10001"
            required
          />
        </div>

        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            MTCN (Money Transfer Control Number)
          </label>
          <input
            type="text"
            value={wuMtcn}
            onChange={(e) => setWuMtcn(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm font-mono"
            placeholder="Auto-generated after transfer"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Lock className="w-3 h-3" />
              SECURITY QUESTION
            </label>
            <input
              type="text"
              value={wuSecurityQuestion}
              onChange={(e) => setWuSecurityQuestion(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="What is your favorite color?"
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Key className="w-3 h-3" />
              SECURITY ANSWER
            </label>
            <input
              type="text"
              value={wuSecurityAnswer}
              onChange={(e) => setWuSecurityAnswer(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="Blue"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderAchFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-green-400 mb-2">
        <Database className="w-4 h-4" />
        ACH TRANSFER (US DOMESTIC)
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs mb-2 flex items-center gap-2">
            <User className="w-3 h-3" />
            ACCOUNT HOLDER NAME *
          </label>
          <input
            type="text"
            value={achAccountHolderName}
            onChange={(e) => setAchAccountHolderName(e.target.value)}
            className="w-full px-4 py-3 terminal-input text-sm"
            placeholder="John Smith"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Hash className="w-3 h-3" />
              ROUTING NUMBER (ABA) *
            </label>
            <input
              type="text"
              value={achRoutingNumber}
              onChange={(e) => setAchRoutingNumber(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              placeholder="021000021"
              maxLength="9"
              required
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <CreditCard className="w-3 h-3" />
              ACCOUNT NUMBER *
            </label>
            <input
              type="text"
              value={achAccountNumber}
              onChange={(e) => setAchAccountNumber(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              placeholder="123456789012"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs mb-2">ACCOUNT TYPE *</label>
            <select
              value={achAccountType}
              onChange={(e) => setAchAccountType(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              required
            >
              <option value="checking">CHECKING</option>
              <option value="savings">SAVINGS</option>
            </select>
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Building className="w-3 h-3" />
              BANK NAME *
            </label>
            <input
              type="text"
              value={achBankName}
              onChange={(e) => setAchBankName(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm"
              placeholder="JPMorgan Chase Bank"
              required
            />
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderS2SFields = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4 pt-4 border-t border-terminal-green/30"
    >
      <div className="flex items-center gap-2 text-sm text-red-400 mb-2">
        <Server className="w-4 h-4" />
        SERVER-TO-SERVER TRANSFER
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          type="button"
          onClick={() => setS2sType('internal')}
          className={`py-3 px-4 text-xs border transition-all ${
            s2sType === 'internal'
              ? 'border-terminal-green bg-terminal-green/20'
              : 'border-terminal-green/50 hover:border-terminal-green'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          INTERNAL
        </button>
        <button
          type="button"
          onClick={() => setS2sType('external')}
          className={`py-3 px-4 text-xs border transition-all ${
            s2sType === 'external'
              ? 'border-terminal-green bg-terminal-green/20'
              : 'border-terminal-green/50 hover:border-terminal-green'
          }`}
        >
          <Wifi className="w-4 h-4 inline mr-2" />
          EXTERNAL
        </button>
      </div>

      {s2sType === 'internal' ? (
        <div className="space-y-4">
          <div>
            <label className="block text-xs mb-2">INTERNAL ACCOUNT ID *</label>
            <input
              type="text"
              value={internalAccountId}
              onChange={(e) => setInternalAccountId(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              placeholder="INT-ACC-XXXX-XXXX"
              required
            />
          </div>
          <div>
            <label className="block text-xs mb-2">DEPARTMENT CODE *</label>
            <input
              type="text"
              value={departmentCode}
              onChange={(e) => setDepartmentCode(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm uppercase"
              placeholder="DEPT-XXX"
              required
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-2 flex items-center gap-2">
                <Radio className="w-3 h-3" />
                SERVER IP ADDRESS *
              </label>
              <input
                type="text"
                value={recipientIp}
                onChange={(e) => setRecipientIp(e.target.value)}
                className="w-full px-4 py-3 terminal-input text-sm font-mono"
                placeholder="192.168.1.100"
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-2">SERVER PORT *</label>
              <input
                type="number"
                value={recipientPort}
                onChange={(e) => setRecipientPort(e.target.value)}
                className="w-full px-4 py-3 terminal-input text-sm"
                placeholder="65433"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-2">USERNAME</label>
              <input
                type="text"
                value={recipientUsername}
                onChange={(e) => setRecipientUsername(e.target.value)}
                className="w-full px-4 py-3 terminal-input text-sm"
                placeholder="server_user"
              />
            </div>
            <div>
              <label className="block text-xs mb-2">PASSWORD</label>
              <input
                type="password"
                value={recipientPassword}
                onChange={(e) => setRecipientPassword(e.target.value)}
                className="w-full px-4 py-3 terminal-input text-sm"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Shield className="w-3 h-3" />
              SERVER CERTIFICATE (Optional)
            </label>
            <textarea
              value={serverCertificate}
              onChange={(e) => setServerCertificate(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              rows="3"
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
            />
          </div>

          <div>
            <label className="block text-xs mb-2 flex items-center gap-2">
              <Key className="w-3 h-3" />
              AUTHENTICATION TOKEN
            </label>
            <input
              type="text"
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              className="w-full px-4 py-3 terminal-input text-sm font-mono"
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6..."
            />
          </div>
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-black text-terminal-green p-4 md:p-8">
      <div className="scanline" />
      
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-terminal-green-dim hover:text-terminal-green mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>BACK TO DASHBOARD</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold cyber-font glow-text">
            TRANSFER CENTER
          </h1>
          <p className="text-terminal-green-dim text-sm mt-2">
            Secure Multi-Protocol Financial Transfer System
          </p>
        </motion.div>

        {success && result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 terminal-card p-6 border-terminal-green"
            data-testid="success-message"
          >
            <div className="flex items-center gap-3 mb-4">
              {result.transaction.status === 'PENDING' ? (
                <>
                  <Clock className="w-6 h-6 text-yellow-500" />
                  <span className="text-xl font-bold text-yellow-500">TRANSFER PENDING APPROVAL</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-6 h-6 text-terminal-green" />
                  <span className="text-xl font-bold">TRANSFER {result.transaction.status}</span>
                </>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-terminal-green-dim">Transaction Reference:</span>
                <span className="font-mono">{result.transaction.transaction_reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-terminal-green-dim">Amount:</span>
                <span className="font-bold">${result.transaction.amount.toLocaleString()} {result.transaction.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-terminal-green-dim">Status:</span>
                <span className={
                  result.transaction.status === 'COMPLETED' ? 'text-terminal-green' : 
                  result.transaction.status === 'PENDING' ? 'text-yellow-500' : 'text-red-500'
                }>
                  {result.transaction.status}
                </span>
              </div>
              {result.transaction.status === 'PENDING' && (
                <div className="mt-4 p-3 border border-yellow-500/50 bg-yellow-500/10 text-yellow-500 text-xs">
                  Your transfer is awaiting admin approval. You will be notified once it's processed.
                </div>
              )}
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6 p-4 border border-red-500 bg-red-500/10 flex items-center gap-2"
            data-testid="error-message"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </motion.div>
        )}

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="terminal-card p-6 md:p-8"
        >
          <div className="space-y-6">
            {/* Account Selection */}
            <div>
              <label className="block text-sm mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                SOURCE ACCOUNT
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => handleAccountChange(e.target.value)}
                className="w-full px-4 py-3 terminal-input"
                required
                data-testid="account-select"
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.account_number} - ${account.balance.toFixed(2)} {account.currency}
                  </option>
                ))}
              </select>
              {selectedAccountData && (
                <div className="mt-2 text-xs text-terminal-green-dim">
                  Available Balance: ${selectedAccountData.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>

            {/* Transfer Type */}
            <div>
              <label className="block text-sm mb-2 flex items-center gap-2">
                <Server className="w-4 h-4" />
                TRANSFER TYPE
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {TRANSFER_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setTransferType(type.id)}
                    className={`py-3 px-2 text-xs border transition-all flex flex-col items-center gap-1 ${
                      transferType === type.id
                        ? 'border-terminal-green bg-terminal-green/20 glow-box'
                        : 'border-terminal-green/50 hover:border-terminal-green'
                    }`}
                    data-testid={`transfer-type-${type.id.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <type.icon className={`w-4 h-4 ${type.color}`} />
                    <span className="text-center leading-tight">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount & Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  AMOUNT
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 terminal-input text-lg font-bold"
                  placeholder="0.00"
                  required
                  data-testid="amount-input"
                />
              </div>

              <div>
                <label className="block text-sm mb-2">CURRENCY</label>
                <input
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 terminal-input uppercase text-lg font-bold"
                  maxLength="3"
                  required
                  data-testid="currency-input"
                />
              </div>
            </div>

            {/* Recipient Info */}
            <div>
              <label className="block text-sm mb-2 flex items-center gap-2">
                <Send className="w-4 h-4" />
                RECIPIENT INFORMATION
              </label>
              <input
                type="text"
                value={recipientInfo}
                onChange={(e) => setRecipientInfo(e.target.value)}
                className="w-full px-4 py-3 terminal-input"
                placeholder="Recipient name or identifier"
                required
                data-testid="recipient-info-input"
              />
            </div>

            {/* Dynamic Fields Based on Transfer Type */}
            <AnimatePresence mode="wait">
              {transferType === 'Terminal' && renderTerminalFields()}
              {transferType === 'SWIFT' && renderSwiftFields()}
              {transferType === 'SEPA' && renderSepaFields()}
              {transferType === 'MoneyGram' && renderMoneyGramFields()}
              {transferType === 'Western Union' && renderWesternUnionFields()}
              {transferType === 'ACH' && renderAchFields()}
              {transferType === 'S2S' && renderS2SFields()}
            </AnimatePresence>

            {/* Admin Notice */}
            {user?.role !== 'admin' && (
              <div className="p-4 border border-yellow-500/50 bg-yellow-500/10 text-yellow-500 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Transfers require admin approval before processing</span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !selectedAccount}
              className="w-full py-4 terminal-button flex items-center justify-center gap-2 disabled:opacity-50 text-lg"
              data-testid="submit-transfer-button"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>PROCESSING TRANSFER...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>INITIATE SECURE TRANSFER</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </div>

      {/* Processing Animations */}
      <AnimatePresence>
        {showProcessing && (
          <TransferProcessingAnimation
            transferType={getActualTransferType()}
            transferData={{
              amount: amount,
              currency: currency,
              from_account: selectedAccountData?.account_number,
              recipient_info: recipientInfo
            }}
            onComplete={handleProcessingComplete}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showS2SConnection && (
          <S2SConnectionAnimation
            recipientIp={recipientIp}
            recipientPort={recipientPort}
            transferData={{
              amount: amount,
              currency: currency,
              from_account: selectedAccountData?.account_number,
              recipient_info: recipientInfo
            }}
            onComplete={handleS2SComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvancedTransferPage;
